package com.klantenstop.web.pages.search

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.klantenstop.web.bindings.MapPin
import com.klantenstop.web.components.AdSlot
import com.klantenstop.web.components.ClusteredMap
import com.klantenstop.web.components.RadiusConfig
import com.klantenstop.web.components.VisuallyHiddenH1
import com.klantenstop.web.components.companyCard.CompanyList
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.utils.UpdateSearchItemListJsonLd
import com.klantenstop.web.components.layout.PageSeoConfig
import com.klantenstop.web.components.search.CuratedLocationGrid
import com.klantenstop.web.components.search.SearchSection
import com.klantenstop.web.data.models.domain.CompanyPin
import com.klantenstop.web.data.models.domain.Coordinates
import com.klantenstop.web.data.models.domain.Radius
import com.klantenstop.web.data.models.domain.SearchParams
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.utils.Constants
import com.klantenstop.web.utils.SiteGlobals
import com.klantenstop.web.utils.searchParamsFromCitySlug
import com.klantenstop.web.utils.toMapPopupHtml
import com.materialkobweb.components.widgets.OutlinedButtonLink
import com.materialkobweb.components.widgets.TextButtonLink
import com.materialkobweb.constants.Constants as MaterialConstants
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.utils.rememberViewModel
import com.varabyte.kobweb.browser.uri.decodeURIComponent
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.VerticalAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexDirection
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.verticalAlign
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.div
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Text

private const val METERS_PER_KM = 1000

private const val RESULTS_SCROLL_MARGIN_EXTRA_PX = 24

// TODO: Determine if this is accessible, or if we want to show a 404 page... https://github.com/C-B-Connect-I-T/KlantenStop-Frontend/issues/279
@Page("/zoeken")
@Composable
fun SearchPage() = SearchPageRoute()

@Page("/zoeken/{categorie}/{stad?}")
@Composable
fun SearchPageWithFilters() = SearchPageRoute()

@Composable
private fun SearchPageRoute() {
    val ctx = rememberPageContext()

    // Use a stable key (not the default route-path key) so the ViewModel is shared across the bare
    // "/zoeken" route and every "/zoeken/{categorie}/{stad?}" variant, instead of a fresh instance
    // (and a fresh category reload) being cached per distinct path.
    val viewModel = rememberViewModel(key = "search-page", cached = true) { SearchViewModel() }
    val state by viewModel.state.collectAsState()

    // Tracks the previous route this effect actually acted on, so we can tell a genuine
    // navigation (new search / page change) apart from the one-time transition that happens
    // when the categories finish loading on first mount -- only the former should scroll.
    var lastProcessedRoute by remember { mutableStateOf<String?>(null) }

    // Read URL params and trigger search when URL changes
    LaunchedEffect(ctx.route.pathQueryAndFragment, state.loading) {
        if (state.loading) return@LaunchedEffect // Wait for categories to load

        val currentRoute = ctx.route.pathQueryAndFragment
        // Pagination/search results replace the whole page content. Scrolling to the top of the
        // results list (not the absolute page top) avoids leaving the map/search form/ad still
        // taking up the viewport above the new results, which would push the actual content the
        // user cares about off-screen.
        if (lastProcessedRoute != null && lastProcessedRoute != currentRoute) {
            val resultsEl = document.getElementById("search-results")
            if (resultsEl != null) {
                resultsEl.scrollIntoView(js("{ block: 'start' }"))
            } else {
                window.scrollTo(0.0, 0.0)
            }
        }
        lastProcessedRoute = currentRoute

        val categorieSegment = ctx.route.params["categorie"]?.let(::decodeURIComponent)?.takeIf { it.isNotBlank() }
        val citySlug = ctx.route.params["stad"]?.let(::decodeURIComponent)?.takeIf { it.isNotBlank() }
        val radiusParam = ctx.route.params["straal"]?.toIntOrNull()
        val radius = Radius.entries.find { it.range == radiusParam } ?: Radius.Km5
        val pageParam = ctx.route.params["pagina"]?.toIntOrNull()?.takeIf { it > 0 } ?: 1

        // A "categorie" segment that doesn't match a known category is ignored rather than reinterpreted as a city
        val matchedCategory = state.categories.find { it.matchesIdentifier(categorieSegment) }

        val urlSearchParams = searchParamsFromCitySlug(citySlug, matchedCategory, radius)

        if (matchedCategory != null || citySlug != null) {
            viewModel.sendIntent(SearchContract.Intent.Search(urlSearchParams, page = pageParam))

            // Normalize the address bar whenever the incoming URL doesn't match our canonical
            // "+"-for-space slug format -- e.g. a user typing "sint - niklaas" directly into the
            // address bar gets auto-percent-encoded by the browser into "sint%20-%20niklaas".
            // Path segments aren't URL-decoded by Kobweb's router, so without this the raw "%20"
            // would otherwise leak into the parsed city name. Comparing against the canonical,
            // re-built route is also what makes this a one-shot, idempotent correction.
            val canonicalRoute = Navigation.Screen.Search(urlSearchParams, pageParam).route
            if (canonicalRoute != ctx.route.pathQueryAndFragment) {
                ctx.router.navigateTo(canonicalRoute, UpdateHistoryMode.REPLACE)
            }
        } else {
            viewModel.sendIntent(SearchContract.Intent.ClearSearch(searchParams = null))
        }
    }

    LaunchedEffect(Unit) {
        viewModel.effect.collectLatest {
            when (it) {
                is SearchContract.Effect.PageOutOfRange -> {
                    state.searchParams?.let { params ->
                        val route = Navigation.Screen.Search(params, it.correctedPage).route
                        ctx.router.navigateTo(route, UpdateHistoryMode.REPLACE)
                    }
                }
            }
        }
    }

    SearchPageContent(state)
}

@Composable
private fun SearchPageContent(
    state: SearchContract.State
) {
    val ctx = rememberPageContext()
    val breakpoint = rememberBreakpoint()

    val currentRouter by rememberUpdatedState(ctx.router)
    val pins = remember(state.pins) { state.pins.toMapPins(currentRouter) }

    // Only populated for a city-level search (see CompanyPinsDto.center) — draws the
    // search-radius circle around the resolved city center.
    val radiusConfig = remember(state.mapCenter, state.searchParams?.radius) {
        state.mapCenter.toRadiusConfig(state.searchParams?.radius)
    }

    // Each results page lists different companies, so pages beyond the first are self-canonical
    val page = ctx.route.params["pagina"]?.toIntOrNull()?.takeIf { it > 1 }
    val searchPath = ctx.route.path.trimEnd('/')
    val canonicalPath = if (page != null) "$searchPath?pagina=$page" else null

    val categoryHeading = state.searchParams?.category?.name?.takeIf { it.isNotBlank() }?.let { name ->
        name + (state.searchParams.query.takeIf { it.isNotBlank() }?.let { " in $it" } ?: "")
    }
    val pageTitle = categoryHeading?.let { if (page != null) "$it - pagina $page" else it }
        ?: "Zoek en filter zorgverleners in jouw buurt"

    val categoryName = state.searchParams?.category?.name?.takeIf { it.isNotBlank() }
    val pageDescription = if (categoryName != null && state.total > 0) {
        val locationPart = state.searchParams?.query?.takeIf { it.isNotBlank() }?.let { " in $it" } ?: ""
        // Pages beyond the first get a unique description, mirroring the backend's bot-facing one
        val totalPages = ((state.total + state.pageSize - 1) / state.pageSize).toInt().coerceAtLeast(1)
        val pageSuffix = if (page != null) " (pagina $page van $totalPages)" else ""
        "Overzicht van ${state.total} bedrijven in de categorie $categoryName$locationPart met hun klantenstop-status op KlantenStop$pageSuffix."
    } else {
        "Zoek huisartsen, tandartsen en andere zorgverleners zonder patiëntenstop. Filter op locatie, categorie en afstand en vermijd lange wachtlijsten."
    }

    UpdateSearchItemListJsonLd(
        heading = categoryHeading,
        canonicalPath = canonicalPath ?: searchPath,
        companies = state.companies,
        total = state.total,
        page = state.page,
        pageSize = state.pageSize,
    )

    PageLayout(
        modifier = Modifier.fillMaxSize(),
        canonicalPath = canonicalPath,
        title = pageTitle,
        seo = PageSeoConfig.Managed(
            description = pageDescription,
            indexable = true,
            keywords = listOf(
                "zorgverlener zoeken",
                "dokter zoeken",
                "huisarts zoeken",
                "tandarts zoeken",
                "zonder patiëntenstop",
                "zonder wachtlijst",
            ),
            breadcrumbLabel = "Zoeken",
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(bottom = if (breakpoint > Breakpoint.SM) 60.px else 24.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Invisible H1 for SEO structure — not displayed to users
            VisuallyHiddenH1("Zoek huisartsen, tandartsen en andere zorgverleners zonder wachtlijst")

            Box(
                modifier = Modifier.fillMaxWidth().height(60.vh).backgroundColor(MaterialColorVars.PrimaryContainer.value())
            ) {
                ClusteredMap(pins = pins, radius = radiusConfig)
            }

            SearchSection(
                categories = state.categories,
                initialSearchParams = state.searchParams ?: com.klantenstop.web.data.models.domain.SearchParams(),
                horizontal = breakpoint >= Breakpoint.SM,
                collapsible = breakpoint < Breakpoint.SM,
                modifier = Modifier.fillMaxWidth(if (breakpoint == Breakpoint.SM) 90.percent else 80.percent)
                    .margin(top = 32.px)
                    .maxWidth(Constants.SECTION_WIDTH.px.div(1.5)),
                validateFields = true
            )

            if (SiteGlobals.environment == "development" || SiteGlobals.adsenseSearchSlotId.isNotBlank()) {
                AdSlot(
                    slotName = "search-results-top",
                    adUnitId = SiteGlobals.adsenseSearchSlotId,
                    modifier = Modifier
                        .fillMaxWidth(if (breakpoint == Breakpoint.SM) 90.percent else 80.percent)
                        .maxWidth(Constants.SECTION_WIDTH.px.div(1.5))
                        .margin(top = 24.px)
                )
            }

            Box(
                modifier = Modifier.fillMaxWidth().margin(top = 64.px, bottom = 64.px)
                    .display(DisplayStyle.Flex)
                    .verticalAlign(VerticalAlign.Middle)
                    .flexDirection(FlexDirection.Row)
                    .justifyContent(JustifyContent.Center)
                    .gap(40.px)
                    .thenIf(breakpoint < Breakpoint.MD) {
                        Modifier.flexDirection(FlexDirection.Column)
                            .alignItems(AlignItems.Center)
                    }
            ) {
                SpanText(
                    modifier = Modifier
                        .whiteSpace(WhiteSpace.PreWrap)
                        .textAlign(TextAlign.Center),
                    text = "Staat het bedrijf dat je zoekt er niet tussen?\nVoeg het toe door te klikken op de knop.",
                )

                OutlinedButtonLink(
                    path = Navigation.Screen.ManageCompany().route
                ) {
                    Text("Bedrijf toevoegen")
                }
            }

            Column(
                modifier = Modifier.fillMaxWidth().id("search-results")
                    .styleModifier {
                        property(
                            "scroll-margin-top",
                            "${MaterialConstants.HEADER_HEIGHT + RESULTS_SCROLL_MARGIN_EXTRA_PX}px"
                        )
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                when {
                    state.searchParams == null -> SearchPromptEmptyState(
                        modifier = Modifier.margin(top = 80.px, bottom = 80.px)
                    )
                    state.companies.isEmpty() -> SearchNoResultsEmptyState(
                        modifier = Modifier.margin(top = 80.px, bottom = 80.px)
                    )
                    else -> {
                        CompanyList(state.companies)

                        if (state.total > state.pageSize) {
                            state.searchParams?.let { params ->
                                SearchPaginationControls(
                                    currentPage = state.page,
                                    totalItems = state.total,
                                    pageSize = state.pageSize,
                                    pageHref = { newPage -> Navigation.Screen.Search(params, newPage).route },
                                    modifier = Modifier.margin(top = 32.px)
                                )
                            }
                        }
                    }
                }
            }

            state.searchParams?.category?.let { category ->
                CuratedLocationGrid(
                    category = category,
                    curatedCities = state.curatedCities,
                    currentLocationSlug = state.searchParams.locationSlug ?: state.searchParams.query,
                    radius = state.searchParams.radius,
                    breakpoint = breakpoint,
                    modifier = Modifier
                        .margin(top = 32.px)
                        .maxWidth(1100.px)
                )
            }
        }
    }
}

// Pins come from the unpaginated map-pins fetch — independent of the paginated card list — so
// the map keeps showing every matching company regardless of which page is selected.
private fun List<CompanyPin>.toMapPins(router: Router): List<MapPin> = map { pin ->
    MapPin(
        lat = pin.address?.coordinates?.latitude ?: 0.0,
        lng = pin.address?.coordinates?.longitude ?: 0.0,
        label = pin.name,
        iconUrl = pin.statusType.toPinIconUrl(),
        popUpContent = pin.toMapPopupHtml(router)
    )
}

// Only meaningful for a city-level search (see CompanyPinsDto.center) — draws the search-radius
// circle around the resolved city center. The radius itself is a value the frontend already has
// (it's part of the request), so it's combined here with the backend-resolved center.
private fun Coordinates?.toRadiusConfig(radius: Radius?): RadiusConfig? = this?.let { center ->
    RadiusConfig(
        centerLat = center.latitude,
        centerLng = center.longitude,
        range = (radius?.range ?: 0) * METERS_PER_KM
    )
}

@Composable
private fun SearchPromptEmptyState(modifier: Modifier = Modifier) {
    SpanText(
        modifier = modifier.textAlign(TextAlign.Center),
        text = "Kies een categorie (optioneel aangevuld met een gemeente) om te beginnen met zoeken.",
    )
}

@Composable
private fun SearchNoResultsEmptyState(modifier: Modifier = Modifier) {
    SpanText(
        modifier = modifier.textAlign(TextAlign.Center),
        text = "Geen bedrijven gevonden. Probeer een andere categorie of gemeente.",
    )
}

@Composable
private fun SearchPaginationControls(
    currentPage: Int,
    totalItems: Long,
    pageSize: Int,
    pageHref: (Int) -> String,
    modifier: Modifier = Modifier
) {
    val totalPages = ((totalItems + pageSize - 1) / pageSize).toInt()

    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        SearchPaginationLink(
            text = "← Vorige",
            href = pageHref(currentPage - 1),
            enabled = currentPage > 1
        )

        SpanText(
            text = "Pagina $currentPage van $totalPages",
            modifier = Modifier
                .padding(leftRight = 24.px)
                .color(MaterialColorVars.OnSurface.value())
        )

        SearchPaginationLink(
            text = "Volgende →",
            href = pageHref(currentPage + 1),
            enabled = currentPage < totalPages
        )
    }
}

@Composable
private fun SearchPaginationLink(text: String, href: String, enabled: Boolean) {
    if (enabled) {
        TextButtonLink(
            path = href,
            size = ButtonSize.SM,
            modifier = Modifier
                .padding(8.px)
                .color(MaterialColorVars.Primary.value())
        ) {
            Text(text)
        }
    } else {
        SpanText(
            text = text,
            modifier = Modifier
                .padding(8.px)
                .color(MaterialColorVars.Outline.value())
                .cursor(Cursor.NotAllowed)
        )
    }
}
